import { i18n } from 'i18next';
import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { Page, Promo } from '~src/domains/common/enums/api';
import {
  DateFormat,
  Keys,
  PaymentOption,
} from '~src/domains/common/enums/common';
import { Country, Locale } from '~src/domains/common/enums/localization';
import { PlaidAccount } from '~src/domains/common/enums/plaid';
import {
  initLockoutTimer,
  setLocale,
  setLockoutTimer,
} from '~src/domains/common/state/commonReducer';
import { XhrResponseType } from '~src/domains/common/types/api';
import { ConfigType, StateObject } from '~src/domains/common/types/common';
import { LoanOfferType } from '~src/domains/common/types/offers';
import { PlaidAccountType } from '~src/domains/common/types/plaid';
import {
  MappedTravelersType,
  TravelerType,
} from '~src/domains/common/types/users';
import { PREFERRED_NUM_PAYMENTS, VALID_MONTH_ENDS } from './constants';
import { trackRaw } from './tracking';

/** Applies a format to the value.
 * @param {string} value - Value.
 * @param {string} mask - Format.
 * @param {string} dropExtraChar - Whether to drop special characters.
 */
const applyMask = (value: string, mask: string, dropExtraChar = false) => {
  const matchMask = (r: any, vIndex: any, mIndex: any) => {
    const vChar = value[vIndex];
    const mChar = mask[mIndex];

    if (
      (mChar === '9' && vChar.match(/\d/g)) ||
      (mChar === 'X' && vChar.match(/[a-zA-Z]/g))
    ) {
      return [r + vChar, vIndex + 1, mIndex + 1];
    }

    if (mChar === '9' || mChar === 'X') {
      return [r, vIndex + 1, mIndex];
    }

    return [r + mChar, vIndex, mIndex + 1];
  };

  const _applyMask = (r: any, vIndex: any, mIndex: any): string => {
    if (!value || vIndex === value.length || mIndex === mask.length) {
      if (
        mIndex < mask.length &&
        mask[mIndex] !== '9' &&
        mask[mIndex] !== 'X'
      ) {
        return r + mask[mIndex];
      }

      return r;
    }

    const [result, newVIndex, newMIndex] = matchMask(r, vIndex, mIndex);

    return _applyMask(result, newVIndex, newMIndex);
  };

  const maskedValue = _applyMask('', 0, 0);
  const isLastSpecialChar = isSpecialChar(
    maskedValue.substring(maskedValue.length - 1),
  );

  return dropExtraChar && isLastSpecialChar
    ? maskedValue.substring(0, maskedValue.length - 1)
    : maskedValue;
};

/** Breaks up a date into day, month, and year.
 * @param {string} date - Date.
 * @param {DateFormat} format - Date format.
 */
const breakupDate = (date: string, format: DateFormat) => {
  switch (format) {
    case DateFormat.MM_DD_YYYY:
      return {
        mm: date.substring(0, 2),
        dd: date.substring(2, 4),
        yyyy: date.substring(4, 8),
      };
    case DateFormat.DD_MM_YYYY:
      return {
        dd: date.substring(0, 2),
        mm: date.substring(2, 4),
        yyyy: date.substring(4, 8),
      };
    case DateFormat.YYYY_MM_DD:
      return {
        mm: date.substring(4, 6),
        dd: date.substring(6, 8),
        yyyy: date.substring(0, 4),
      };
    default:
      return {};
  }
};

/** Selects a default offer from a list of offers with the following precedence:
 * 1. Offers that have 'pre-select' in the promos.
 * 2. If on accept page or reconfirm page and the number of payments in the offer equal the preferred num of payments.
 * 3. The first offer.
 * @param {LoanOfferType[]} offers - List of offers.
 * @param {Page} page - Page.
 */
const chooseDefaultOffer = (offers: LoanOfferType[], page: Page) => {
  const preferredOffer =
    (page === Page.ACCEPT || page === Page.RECONFIRM) &&
    offers.find(offer => offer.number_of_payments === PREFERRED_NUM_PAYMENTS);
  const preselectedOffer = offers.find(offer =>
    offer.promos?.includes(Promo.PRE_SELECT),
  );

  return preselectedOffer || preferredOffer || offers[0];
};

/**
 * Reformats dates given a current and output format.
 * @param {string} value - Date value.
 * @param {DateFormat} currentFormat - Current date format.
 * @param {DateFormat} outputFormat - Desired date format.
 * @returns {string} - Date with mask (e.g. 04071963 -> 04/07/1963 | 07/04/1963 | 1963/04/07)
 */
const formatDate = (
  value: string,
  currentFormat: DateFormat,
  outputFormat?: DateFormat,
) => {
  const rawDate = value.replace(/[^\d]*/g, '');
  const { dd, mm, yyyy } = breakupDate(rawDate, currentFormat);
  let formattedDate;

  switch (outputFormat) {
    case DateFormat.DD_MM_YYYY:
      formattedDate = `${dd}${mm}${yyyy}`;
      break;
    case DateFormat.MM_DD_YYYY:
    default:
      formattedDate = `${mm}${dd}${yyyy}`;
  }

  return applyMask(formattedDate, '99/99/9999');
};

/** Gets country date format.
 * @param {Country} country - Country to get date format for.
 * @returns {DateFormat} - Date format for the country.
 */
const getCountryDateFormat = (country: Country) => {
  const countryDateFormatMap = {
    [Country.CA]: DateFormat.DD_MM_YYYY,
    [Country.US]: DateFormat.MM_DD_YYYY,
  };

  return countryDateFormatMap[country] || DateFormat.MM_DD_YYYY;
};

/** Generates the merchant config from the XHR response object.
 * @param {XhrResponseType} response - Response object.
 * @param {ConfigType} config - Configuration for certain merchants.
 */
const getMerchantConfig = (response: XhrResponseType, config: ConfigType) => {
  const orderInfo = response.order_info;
  const upcode = orderInfo.up_code;
  const country = orderInfo.localization.country;
  const paymentOptions = [PaymentOption.BANK_ACCOUNT, PaymentOption.DEBIT];

  if (
    country === Country.CA &&
    config.overWriteShowCreditUpcs.includes(upcode)
  ) {
    paymentOptions.push(PaymentOption.CREDIT);
  }

  let result: any = {
    merchant: response.merchant_name,
    onboardSpend: response.ccs_config['onboard-spend'],
    paymentOptions,
    upcode,
  };

  if (response.ccs_config['subvention-promo']) {
    result.subventionPromo = {
      requireCampaign:
        response.ccs_config['subvention-promo']['require-campaign'],
    };
  }

  return result;
};

/** Initializes and runs the lockout timer in state. */
const initAndRunLockoutTimer =
  () =>
  (
    dispatch: ThunkDispatch<StateObject, unknown, Action<string>>,
    getState: Function,
  ) => {
    const state = getState();

    const lockoutMultiplier = state.common.lockoutMultiplier;
    const lockoutSeconds = Math.min(lockoutMultiplier * 15, 60);

    dispatch(initLockoutTimer(lockoutSeconds));
    dispatch(runLockoutTimer(lockoutSeconds));
    dispatch(trackRaw('kasa-lock-out', lockoutSeconds));
  };

/** Determines whether a given character is a special character (non-alphanumerical character)
 * @param {string} character - Single character.
 * @returns {boolean} Wheter the character is a special character.
 */
const isSpecialChar = (character: string) => {
  return !character.match(/(\d|[a-zA-Z])/g);
};

/** Transforms an array of travelers to an object of travelers with the keys as the respective traveler ids.
 * @param {TravelerType[]} travelers - List of travelers.
 */
const mapTravelers = (travelers: TravelerType[]) => {
  const travelerMap: MappedTravelersType = {};

  for (const traveler of travelers) {
    travelerMap[traveler.id] = traveler;
  }

  return travelerMap;
};

/** Sets lockout timer in state, then runs down the timer. */
const runLockoutTimer =
  (lockoutSeconds: number) =>
  (dispatch: ThunkDispatch<StateObject, unknown, Action<string>>) => {
    if (!lockoutSeconds) return;

    dispatch(setLockoutTimer(lockoutSeconds - 1));

    setTimeout(() => {
      dispatch(runLockoutTimer(lockoutSeconds - 1));
    }, 1000);
  };

/** Selects a bank account from an array of accounts with the following priority:
 * 1. If only one bank account, select the bank account.
 * 2. If multiple bank accounts, take the first checking account, or the first savings account if no checking account.
 * 3. If no checking or savings accounts, take the first bank account. */
const selectBankAccount = (accounts: PlaidAccountType[]) => {
  const accountsCount = accounts.length;
  const firstAccount = accounts[0];

  if (accountsCount === 1) {
    return firstAccount;
  }

  const checkingAccount = accounts.find(
    account => account.subtype === PlaidAccount.CHECKING,
  );
  const savingsAccount = accounts.find(
    account => account.subtype === PlaidAccount.SAVINGS,
  );

  return checkingAccount || savingsAccount || firstAccount;
};

/** Sorts loan offers by the number of payments, from highest to lowest.
 * @param {LoanOfferType[]} offers - List of offers.
 */
const sortLoanOffers = (offers: LoanOfferType[]) => {
  if (offers.length < 2) {
    return offers;
  }

  return [...offers].sort((precedingOffer, offer) =>
    precedingOffer.number_of_payments < offer.number_of_payments
      ? 1
      : precedingOffer.number_of_payments > offer.number_of_payments
      ? -1
      : 0,
  );
};

/** Converts serialized HTML (string) into an HTML element.
 * @param {string} str - Serialized HTML
 * @returns {HTMLElement} - HTML element.
 */
const stringToHTML = (str: string) => {
  const dom = document.createElement('div');
  dom.innerHTML = str;
  return dom;
};

/** Converts kebab cased strings into lower cased, separated strings.
 * e.g. 'KEBAB-CASE' -> 'kebab case', 'kEbAb-CaSe' -> 'k eb ab ca se'
 */
const separateKebabCase = (str: string) => {
  const matches = str.match(
    /[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g,
  );
  return matches && matches.map(x => x.toLowerCase()).join(' ');
};

/** Verifies whether a year is a leap year. */
const isLeapYear = (year: number) => {
  return year % 400 === 0 || (year % 100 !== 0 && year % 4 === 0);
};

/** Gets the correct number of days in a month. */
const getValidMonthEnd = (month: string, year: string) => {
  const intMonth = parseInt(month);

  if (isLeapYear(parseInt(year)) && intMonth === 2) {
    return 29;
  }

  return VALID_MONTH_ENDS[intMonth - 1];
};

/** Verifies the validity of a date. Date must be in 'mm/dd/yyyy' format. */
const isValidDate = (value: string) => {
  const [mm, dd, yyyy] = value.split('/');
  return (
    parseInt(mm) >= 1 &&
    parseInt(mm) <= 12 &&
    parseInt(dd) >= 1 &&
    parseInt(dd) <= getValidMonthEnd(mm, yyyy) &&
    parseInt(yyyy) > 0
  );
};

/** Updates locale. */
const changeLocale =
  (locale: Locale, i18n: i18n) => (dispatch: Function, getState: Function) => {
    const state = getState();
    const prevLocale = state.common.locale;

    if (prevLocale === locale) {
      return;
    }

    dispatch(setLocale(locale));
    i18n.changeLanguage(locale);
  };

/** Converts strings with spaces into lower case + kebab case with an optional prefix.
 * e.g. 'Arthur Davis' -> 'arthur-davis'
 * @param {string} label - String to convert.
 * @param {string} [prefix] - Optional prefix.
 * @returns {string} String in lower case kebab format.
 */
const toKebabCase = (label: string, prefix: string = '') => {
  return prefix + label.toLowerCase().replace(/\s+/g, '-');
};

const toTitleCase = (str: string) =>
  str.replace(
    /\w\S*/g,
    txt => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase(),
  );

/**
 * Post process functions used to format the payload value.
 * Define one of the keys as `postProcess` in the dictionary if neessary.
 */
const postProcessFunctions: { [key: string]: Function } = {
  reformatDateOfBirthUS: (v: string) => formatDate(v, DateFormat.MM_DD_YYYY),
  reformatDateOfBirthCA: (v: string) =>
    formatDate(v, DateFormat.DD_MM_YYYY, DateFormat.MM_DD_YYYY),
  priceInCents: (v: string) => parseFloat(v.replace(/[^\d.]*/g, '')) * 100,
  digitOnlyString: (v: string) => v.replace(/[^\d.]*/g, ''), // for mobile number formatting
  digitCharOnlyString: (v: string) => v.replace(/[^A-Za-z0-9]*/g, ''), // for postal code formatting
};

/**
  Add this handler as keydown event listerner to trap the tabbing inside
  the parentElement and call escapeHandler when escape key is pressed.
*/
const modalKeydownHandler =
  (parentSelector: string, escapeHandler: Function) => (e: KeyboardEvent) => {
    const tabbableElements = document.querySelectorAll(
      `${parentSelector} [tabindex=\"0\"]`,
    );
    const lastElement = tabbableElements[
      tabbableElements.length - 1
    ] as HTMLElement;
    const firstElement = tabbableElements[0] as HTMLElement;
    const key = e.key;
    const target = e.target as HTMLElement;
    const activeElement = document.activeElement;

    if (key === Keys.ESCAPE || key === Keys.ESC) {
      escapeHandler();
    } else if (key === Keys.TAB) {
      if (e.shiftKey) {
        if (activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        if (activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    } else {
      if (target) target.focus();
    }
  };

/** Format first and last name from a given customer name object */
const formatCustomerNames = (customerInfo: {
  firstName?: string;
  lastName?: string;
}) => {
  const { firstName = '', lastName = '' } = customerInfo;

  return {
    firstName: firstName.trim(),
    lastName: lastName.trim(),
  };
};

/**  Converts a map of options into an array of options with the key as the index. Sorted by value if sort=true, in ascending order.
 * E.g. { a: '123', b: '456' } => [{ value: '123', index: 'a' }, { value: '456', index: 'b' }]
 */
const getOptionsList = (options: { [key: string]: any }, sort?: boolean) => {
  const optionEntries = Object.entries(options).map(([key, value]) => ({
    value,
    index: key,
  }));

  return sort
    ? optionEntries.sort((option, nextOption) => {
        return option.value < nextOption.value ? -1 : 1;
      })
    : optionEntries;
};

const showPromoBanner = (
  subventionPromo: { requireCampaign: boolean } | false,
  campaign: Array<Object> | [] = [],
) => {
  if (!subventionPromo) return false;
  return (
    !subventionPromo.requireCampaign ||
    (subventionPromo.requireCampaign && campaign?.length)
  );
};

export {
  applyMask,
  breakupDate,
  chooseDefaultOffer,
  formatDate,
  getCountryDateFormat,
  getMerchantConfig,
  initAndRunLockoutTimer,
  isSpecialChar,
  mapTravelers,
  runLockoutTimer,
  selectBankAccount,
  sortLoanOffers,
  stringToHTML,
  separateKebabCase,
  isLeapYear,
  getValidMonthEnd,
  isValidDate,
  changeLocale,
  toKebabCase,
  toTitleCase,
  postProcessFunctions,
  modalKeydownHandler,
  formatCustomerNames,
  getOptionsList,
  showPromoBanner,
};
